/** Paginated conversation list (left pane): ONE page (20) of parent conversations; Prev/Next refetch the adjacent page. A row click opens the thread. No messages are fetched here. */

import { useState, type CSSProperties } from 'react';
import { ARIA_ROLES } from '@federa/components/common/aria-tokens';
import { Button } from '@federa/components/primitives/Button';
import { Input } from '@federa/components/inputs/Input';
import { spacing } from '@federa/components/css-refs.spacing';
import { typography } from '@federa/components/css-refs.typography';
import { borderWidth } from '@federa/components/css-refs.effects';
import { useKeyboardClick } from '@federa/components/hooks/useKeyboardClick';
import { KEYBOARD_KEYS } from '@federa/const/keyboard-events';
import { loadingMessages } from '@federa/utils/styles/content-tokens';
import {
	FLEX_BETWEEN_STYLE,
	FLEX_COLUMN_STYLE,
	FLEX_FILL_SCROLL_Y_STYLE,
	FLEX_FILL_TRUNCATE_STYLE,
} from '@federa/const/style-flex-presets';
import { FLEX_ROW_GAP2_STYLE } from '@federa/components/common/style-primitives';
import { LABEL_PREV } from '@federa/const/label-values';
import { TEXT_TRUNCATE_STYLE } from '@federa/const/style-text-presets';
import { VIEWER_SHELL_COLORS, viewerVar } from './chats-viewer-tokens.ts';
import { channelColor, conversationTitle, formatTime } from './format.ts';
import type { ChatViewerConversation } from '@federa/types/conversations';
import type { ListData } from '@federa/types/spa-chats-shapes';

interface Props {
	list: ListData;
	selectedId: string | null;
	onSelect: (conversation: ChatViewerConversation) => void;
	onPageChange: (page: number) => void;
	onSearch: (query: string) => void;
}

const aside: CSSProperties = {
	width: spacing[90],
	minWidth: spacing[75],
	...FLEX_COLUMN_STYLE,
	borderRight: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
	background: viewerVar('--surface'),
};
const headerWrap: CSSProperties = {
	...FLEX_COLUMN_STYLE,
	gap: spacing[2.5],
	padding: `${spacing[4]} ${spacing[4.5]} ${spacing[3.5]}`,
	borderBottom: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
};
const headerTop: CSSProperties = { display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' };
// NO local `searchRow`: it was the baseless `{ display: 'flex', gap: spacing[2] }` gap-ladder rung respelled under a
// local name; the search/clear toolbar reads `FLEX_ROW_GAP2_STYLE` from the ladder itself.
/** Chrome shared by the search/clear toolbar button pair -- the viewer's own squared corner and label weight, which the
 *  `Button` pill and medium weight would otherwise replace. Padding, border and colour stay per-button; fill, cursor
 *  and the disabled treatment are the variant's. */
const toolbarBtnBase: CSSProperties = {
	borderRadius: viewerVar('--radius-sm'),
	fontWeight: typography.fontWeight.semibold,
	fontSize: typography.fontSize.sm,
	whiteSpace: 'nowrap',
};
/** `backgroundColor`, never the `background` shorthand: `Button` repaints on hover by writing that longhand, and a
 *  shorthand beside it makes which one wins depend on CSSOM declaration order rather than on this file. */
const searchBtn: CSSProperties = {
	...toolbarBtnBase,
	padding: `${spacing[2]} ${spacing[3.5]}`,
	border: `${borderWidth.DEFAULT} solid ${viewerVar('--primary')}`,
	color: VIEWER_SHELL_COLORS.onAccent,
};
const clearBtn: CSSProperties = {
	...toolbarBtnBase,
	padding: `${spacing[2]} ${spacing[3]}`,
	border: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
	backgroundColor: viewerVar('--surface'),
	color: viewerVar('--text-muted'),
};
const titleStyle: CSSProperties = {
	fontSize: typography.fontSize.lg,
	fontWeight: typography.fontWeight.bold,
	letterSpacing: typography.letterSpacing.primary,
};
const pageTag: CSSProperties = {
	fontSize: typography.fontSize.xs,
	color: viewerVar('--text-muted'),
	fontWeight: typography.fontWeight.semibold,
};
const state: CSSProperties = {
	padding: `${spacing[7]} ${spacing[5]}`,
	color: viewerVar('--text-muted'),
	fontSize: typography.fontSize.sm,
};
const footer: CSSProperties = {
	...FLEX_BETWEEN_STYLE,
	gap: spacing[2.5],
	padding: `${spacing[3]} ${spacing[4]}`,
	borderTop: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
	background: viewerVar('--surface-2'),
};
const footerLabel: CSSProperties = { fontSize: typography.fontSize.xs, color: viewerVar('--text-faint') };

/** The footer-local delta over an `outline` `Button`: the viewer's squared corner, hairline and label weight.
 *
 * NO ENABLED/DISABLED PAIR. The colour swap and the `not-allowed` cursor this const used to compute are the primitive's
 * disabled treatment now (dimmed, pointer-events off, `disabled` on the element), so a step that cannot be taken is
 * announced as unavailable rather than merely painted grey. */
const navButton: CSSProperties = {
	padding: `${spacing[2]} ${spacing[4]}`,
	borderRadius: viewerVar('--radius-sm'),
	border: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
	backgroundColor: viewerVar('--surface'),
	color: viewerVar('--text'),
	fontWeight: typography.fontWeight.semibold,
	fontSize: typography.fontSize.sm,
};

function rowStyle(selected: boolean): CSSProperties {
	return {
		display: 'flex',
		gap: spacing[3],
		padding: `${spacing[3.5]} ${spacing[4.5]}`,
		cursor: 'pointer',
		borderBottom: `${borderWidth.DEFAULT} solid ${viewerVar('--border')}`,
		borderLeft: `${borderWidth[4]} solid ${selected ? viewerVar('--primary') : 'transparent'}`,
		background: selected ? viewerVar('--primary-soft') : 'transparent',
	};
}
const dot = (color: string): CSSProperties => ({
	width: spacing[2.5],
	height: spacing[2.5],
	borderRadius: '50%',
	background: color,
	marginTop: spacing[1.5],
	flexShrink: 0,
});
const rowTop: CSSProperties = { display: 'flex', justifyContent: 'space-between', gap: spacing[2], alignItems: 'baseline' };
const convTitle: CSSProperties = {
	fontWeight: typography.fontWeight.semibold,
	fontSize: typography.fontSize.sm,
	...TEXT_TRUNCATE_STYLE,
};
const convTime: CSSProperties = {
	fontSize: typography.fontSize.xs,
	color: viewerVar('--text-faint'),
	whiteSpace: 'nowrap',
	flexShrink: 0,
};
const snippet: CSSProperties = {
	fontSize: typography.fontSize.xs,
	color: viewerVar('--text-muted'),
	...TEXT_TRUNCATE_STYLE,
	marginTop: spacing[0.5],
};
const countTag: CSSProperties = {
	fontSize: typography.fontSize.xs,
	color: viewerVar('--text-faint'),
	marginTop: spacing[0.75],
};

function ConversationRow({
	conv,
	selected,
	onSelect,
}: {
	conv: ChatViewerConversation;
	selected: boolean;
	onSelect: (c: ChatViewerConversation) => void;
}) {
	const { handleClick, handleKeyDown } = useKeyboardClick({ onClick: () => onSelect(conv) });
	return (
		<div style={rowStyle(selected)} onClick={handleClick} onKeyDown={handleKeyDown} role={ARIA_ROLES.button} tabIndex={0} title={conv.id}>
			<span style={dot(channelColor(conv.channel))} />
			<div style={FLEX_FILL_TRUNCATE_STYLE}>
				<div style={rowTop}>
					<span style={convTitle}>{conversationTitle(conv.friendlyName, conv.id)}</span>
					<span style={convTime}>{formatTime(conv.lastMessageAt)}</span>
				</div>
				<div style={snippet}>{conv.lastMessage ?? 'No messages yet'}</div>
				<div style={countTag}>{conv.totalMessages} messages</div>
			</div>
		</div>
	);
}

/** Title + the on-demand search bar (search runs on button click / Enter, not while typing). */
function ListHeader({ list, onSearch }: { list: ListData; onSearch: (q: string) => void }) {
	const [q, setQ] = useState('');
	const submit = (): void => onSearch(q);
	const clear = (): void => {
		setQ('');
		onSearch('');
	};
	return (
		<div style={headerWrap}>
			<div style={headerTop}>
				<h1 style={titleStyle}>Conversations</h1>
				<span style={pageTag}>{list.searchQuery !== null ? `${list.items.length} found` : `Page ${list.page + 1}`}</span>
			</div>
			<div style={FLEX_ROW_GAP2_STYLE}>
				{/* All this row still decides about the search box: that it takes the free width and may shrink. The box itself --
				    padding, radius, border, surface, type scale, focus outline, colour -- is `Input`'s as of the 2026-08-22 SDL
				    substitution, which also retires four raw `var(--...)` reads the SDL supplies through tokens. */}
				<Input
					style={FLEX_FILL_TRUNCATE_STYLE}
					placeholder="Search by username or phone"
					value={q}
					onChange={(e) => setQ(e.target.value)}
					onKeyDown={(e) => {
						if (e.key === KEYBOARD_KEYS.ENTER) submit();
					}}
				/>
				<Button variant="primary" size="md" style={searchBtn} onClick={submit}>
					Search
				</Button>
				{list.searchQuery !== null && (
					<Button variant="outline" size="md" style={clearBtn} onClick={clear}>
						Clear
					</Button>
				)}
			</div>
		</div>
	);
}

function ConversationListBody({
	list,
	selectedId,
	onSelect,
}: {
	list: ListData;
	selectedId: string | null;
	onSelect: (c: ChatViewerConversation) => void;
}) {
	if (list.error) return <div style={state}>{list.error}</div>;
	if (list.loading) return <div style={state}>{loadingMessages.default}</div>;
	if (list.items.length === 0)
		return <div style={state}>{list.searchQuery !== null ? 'No conversations for that user.' : 'No conversations on this page.'}</div>;
	return (
		<>
			{list.items.map((c) => (
				<ConversationRow key={c.id} conv={c} selected={c.id === selectedId} onSelect={onSelect} />
			))}
		</>
	);
}

/** One pagination step in the list footer -- Prev and Next differ only in label, enabled predicate and target page. */
function PageNavButton(props: { label: string; enabled: boolean; onClick: () => void }) {
	return (
		<Button variant="outline" size="md" style={navButton} disabled={!props.enabled} onClick={props.onClick}>
			{props.label}
		</Button>
	);
}

export function ConversationList({ list, selectedId, onSelect, onPageChange, onSearch }: Props) {
	return (
		<aside style={aside}>
			<ListHeader list={list} onSearch={onSearch} />
			<div style={FLEX_FILL_SCROLL_Y_STYLE}>
				<ConversationListBody list={list} selectedId={selectedId} onSelect={onSelect} />
			</div>
			{list.searchQuery === null && (
				<div style={footer}>
					<PageNavButton label={LABEL_PREV} enabled={list.page > 0 && !list.loading} onClick={() => onPageChange(list.page - 1)} />
					<span style={footerLabel}>{list.items.length} shown</span>
					<PageNavButton label="Next" enabled={list.hasMore && !list.loading} onClick={() => onPageChange(list.page + 1)} />
				</div>
			)}
		</aside>
	);
}
